import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faCreditCard, faFileInvoice, faFileContract, faArrowLeft, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import ProfileSettings from './ProfileSettings';
import PasswordSettings from './PasswordSettings';
import PaymentSettings from './PaymentSettings';
import BillingSettings from './BillingSettings';
import TermsSettings from './TermsSettings';
import PreferencesSettings from './PreferencesSettings';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const { refreshBusinessProfile } = useAuth();
  const currentLang = lang || 'tr';
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  const hasFetchedRef = useRef(false);
  
  // Helper function to add language prefix to paths
  const getLocalizedPath = (path: string) => {
    return `/${currentLang}${path}`;
  };

  // Fetch business profile when settings page loads - always fetch fresh data
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      // Only fetch once per page load
      if (hasFetchedRef.current) {
        console.log('ℹ️ SettingsPage: Already fetched, skipping...');
        return;
      }

      try {
        hasFetchedRef.current = true;
        console.log('🚀 SettingsPage: Fetching fresh business profile on page load...');
        await refreshBusinessProfile();
        console.log('✅ SettingsPage: Business profile fetched successfully');
      } catch (err) {
        console.error('❌ SettingsPage: Failed to fetch business profile:', err);
        hasFetchedRef.current = false; // Reset on error so it can retry
      }
    };

    fetchBusinessProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - refreshBusinessProfile is stable from context

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const tabs = [
    { id: 'profile', label: t('profile'), icon: faUser },
    { id: 'password', label: t('password'), icon: faLock },
    { id: 'payment', label: t('payment'), icon: faCreditCard },
    { id: 'billing', label: t('billing'), icon: faFileInvoice },
    { id: 'preferences', label: t('preferences'), icon: faGlobe },
    { id: 'terms', label: t('terms'), icon: faFileContract }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'password':
        return <PasswordSettings />;
      case 'payment':
        return <PaymentSettings />;
      case 'billing':
        return <BillingSettings />;
      case 'preferences':
        return <PreferencesSettings />;
      case 'terms':
        return <TermsSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <>
      <SEO {...seoConfig.settings} />
      <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                onClick={() => navigate(getLocalizedPath('/social-media'))}
                className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
                <span className="hidden sm:inline">{t('backToDashboard')}</span>
              </button>
            </div>
            <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {t('title')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Left Sidebar - Tabs */}
          <div className="lg:col-span-1">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-white mb-4 md:mb-6">{t('accountSettings')}</h2>
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      navigate(`${getLocalizedPath('/settings')}?tab=${tab.id}`, { replace: true });
                    }}
                    className={`w-full flex items-center space-x-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl text-left transition-all duration-300 text-sm md:text-base ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20 border border-purple-400/30 text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FontAwesomeIcon 
                      icon={tab.icon} 
                      className={`text-sm ${activeTab === tab.id ? 'text-purple-400' : 'text-white/50'}`} 
                    />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-3">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 lg:p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default SettingsPage;
